// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Renderer for the SRM Expedited Eligibility determination.
 *
 * Reads the `eligibility` dataset slot, which carries a single `determination`
 * (the overall verdict) plus a per-criterion audit trail. Renders a verdict
 * banner and the criteria checklist, surfacing triggered/unclear criteria with
 * their verbatim evidence.
 */
import { type FC } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, HelpCircle, ShieldCheck, ShieldX } from 'lucide-react'
import { Badge } from '@/components/ui'
import type { DocumentSlotData, RendererComponentProps } from '@/app-shell/renderer-components'
import { useDatasets } from '@/subsystems/pipelines'
import {
  buildChecklist,
  byDecisionRelevance,
  citation,
  criteriaSummary,
  fetchKbCriteria,
  parseDetermination,
  parseCriteria,
  type CriterionStatus,
  type EligibilityCriterion,
  type Determination,
  type KbCriterion,
} from '../utils/eligibility'

const VERDICT_STYLES: Record<string, { className: string; Icon: typeof ShieldCheck; label: string }> = {
  'EXPEDITED-ELIGIBLE': {
    className: 'border-green-500 bg-green-500/10 text-green-600',
    Icon: ShieldCheck,
    label: 'Expedited Eligible',
  },
  'NOT ELIGIBLE - ROUTE TO FULL SRM': {
    className: 'border-destructive bg-destructive/10 text-destructive',
    Icon: ShieldX,
    label: 'Not Eligible — Route to Full SRM',
  },
  'INSUFFICIENT INFORMATION': {
    className: 'border-yellow-500 bg-yellow-500/10 text-yellow-600',
    Icon: HelpCircle,
    label: 'Insufficient Information',
  },
}

function verdictStyle(verdict: string) {
  return (
    VERDICT_STYLES[verdict.toUpperCase()] ?? {
      className: 'border-muted bg-muted/20 text-muted-foreground',
      Icon: HelpCircle,
      label: verdict,
    }
  )
}

const STATUS_BADGE: Record<CriterionStatus, string> = {
  PRESENT: 'border-destructive bg-destructive/10 text-destructive',
  UNCLEAR: 'border-yellow-500 bg-yellow-500/10 text-yellow-600',
  ABSENT: 'border-green-500 bg-green-500/10 text-green-600',
}

const STATUS_ICON: Record<CriterionStatus, typeof AlertTriangle> = {
  PRESENT: AlertTriangle,
  UNCLEAR: HelpCircle,
  ABSENT: CheckCircle2,
}

const CriterionRow: FC<{ finding: EligibilityCriterion }> = ({ finding }) => {
  const Icon = STATUS_ICON[finding.status]
  const muted = finding.status === 'ABSENT'
  return (
    <div className={`rounded-lg border overflow-hidden ${muted ? 'bg-card/50' : 'bg-card'}`}>
      <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 bg-muted/30 border-b">
        <Icon
          className={`h-4 w-4 shrink-0 ${
            finding.status === 'PRESENT'
              ? 'text-destructive'
              : finding.status === 'UNCLEAR'
                ? 'text-yellow-600'
                : 'text-green-600'
          }`}
        />
        <span className="text-sm font-medium text-foreground min-w-0">{finding.knowledgeItem}</span>
        <Badge variant="outline" className={`font-semibold text-xs ml-auto shrink-0 ${STATUS_BADGE[finding.status]}`}>
          {finding.status}
        </Badge>
      </div>

      {(finding.reason || finding.recommendation) && (
        <div className="px-4 py-3 space-y-3">
          {finding.reason && <p className="text-sm text-foreground">{finding.reason}</p>}

          {finding.recommendation && (
            <div className="flex gap-2 bg-muted/50 rounded-md p-3">
              <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-foreground min-w-0">{finding.recommendation}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const DeterminationBlock: FC<{ determination: Determination | null; criteria: EligibilityCriterion[] }> = ({
  determination,
  criteria,
}) => {
  const sorted = byDecisionRelevance(criteria)

  if (!determination && criteria.length === 0) {
    return (
      <div className="bg-muted/10 px-4 py-3 text-sm text-muted-foreground">No determination yet.</div>
    )
  }

  const style = determination ? verdictStyle(determination.verdict) : null

  return (
    <div className="space-y-3">
      {determination && style && (
        <div className={`rounded-lg border p-4 ${style.className}`}>
          <div className="flex items-center gap-2">
            <style.Icon className="h-5 w-5 shrink-0" />
            <span className="font-semibold">{style.label}</span>
            <span className="ml-auto text-xs font-medium">{criteriaSummary(criteria)}</span>
          </div>
          {determination.rationale && (
            <p className="text-sm text-foreground mt-2">{determination.rationale}</p>
          )}
          {determination.recommendation && (
            <p className="text-sm font-medium text-foreground mt-2">{determination.recommendation}</p>
          )}
          {determination.decisiveText && (
            <p className="text-sm italic text-muted-foreground mt-2">
              "{determination.decisiveText}"
              {citation(determination) && (
                <span className="not-italic text-xs text-muted-foreground/70"> · {citation(determination)}</span>
              )}
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        {sorted.map((finding, idx) => (
          <CriterionRow key={finding.knowledgeItem + idx} finding={finding} />
        ))}
      </div>
    </div>
  )
}

const ELIGIBILITY_SLOT = 'eligibility'

/**
 * The request's verdict, from whichever document carries the determination.
 *
 * A track holds several documents and only the intake form has a determination, so callers
 * that need the verdict — to explain what the rest of the review did or did not do — read it
 * across the whole slot data rather than from one document.
 */
export function readVerdict(documentSlotData: DocumentSlotData[]): string | null {
  for (const d of documentSlotData) {
    const determination = parseDetermination(d.slotData[ELIGIBILITY_SLOT])
    if (determination) return determination.verdict
  }
  return null
}

/** The workspace's static criteria list, in KB order. The model only reports the criteria it
 *  flags; this list is what the checklist enumerates. */
function useKbCriteria(
  orgId: string,
  workspaceId: string,
  kbInstanceId: string | null,
  apiClient: RendererComponentProps['apiClient'],
): KbCriterion[] {
  const { data } = useQuery<KbCriterion[]>({
    queryKey: ['srm-eligibility-kb', orgId, workspaceId, kbInstanceId],
    enabled: Boolean(kbInstanceId),
    staleTime: 5 * 60_000,
    queryFn: () => fetchKbCriteria(apiClient, orgId, workspaceId, kbInstanceId!),
  })
  return data ?? []
}

export const SrmEligibilityRenderer: FC<RendererComponentProps> = ({
  renderer,
  orgId,
  workspaceId,
  apiClient,
  documentSlotData = [],
}) => {
  const { datasets } = useDatasets(orgId, workspaceId)
  const slotDef = renderer.slots.find((s) => s.key === ELIGIBILITY_SLOT && s.type === 'dataset')
  const kbInstanceId = datasets.find((d) => d.key === slotDef?.datasetKey)?.knowledgeBase ?? null
  const kbCriteria = useKbCriteria(orgId, workspaceId, kbInstanceId, apiClient)

  // Only the documents that actually carry the eligibility dataset. A track also holds
  // documents fed to a separately-triggered pipeline (e.g. the vendor terms agreement),
  // and those have no determination — rendering them would repeat the whole criteria
  // checklist as ABSENT under a blank verdict.
  const determined = documentSlotData.filter((d) => d.slotData[ELIGIBILITY_SLOT])

  if (determined.length === 0) {
    return (
      <div className="bg-muted/10 px-4 py-3 text-sm text-muted-foreground">No determination yet.</div>
    )
  }

  return (
    <div className="bg-muted/10 px-4 py-3 space-y-4">
      {determined.map((d) => {
        const slot = d.slotData[ELIGIBILITY_SLOT]
        return (
          <DeterminationBlock
            key={d.documentId}
            determination={parseDetermination(slot)}
            criteria={buildChecklist(kbCriteria, parseCriteria(slot))}
          />
        )
      })}
    </div>
  )
}
